import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { Bell, ChevronDown, ChevronRight, LogOut, Search, Settings, Shield, User } from "lucide-react";
import type { UserProfile } from "@/features/account_manager/models/UserProfile";
import { useAuth } from "@/features/login/hooks/useAuth";
import { ADMIN_SEARCH_INDEX, getBreadcrumbs } from "../lib/adminNav";

function useClickOutside<T extends HTMLElement>(onOutside: () => void) {
  const ref = useRef<T>(null);
  useEffect(() => {
    function handler(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) onOutside();
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onOutside]);
  return ref;
}

export default function AdminHeader({ user }: { user: UserProfile }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const searchRef = useClickOutside<HTMLDivElement>(() => setSearchOpen(false));
  const userMenuRef = useClickOutside<HTMLDivElement>(() => setUserMenuOpen(false));
  const notificationsRef = useClickOutside<HTMLDivElement>(() => setNotificationsOpen(false));

  const breadcrumbs = getBreadcrumbs(location.pathname);
  const trimmedQuery = query.trim().toLowerCase();
  const results = trimmedQuery
    ? ADMIN_SEARCH_INDEX.filter((item) => item.label.toLowerCase().includes(trimmedQuery))
    : [];

  const goTo = (path: string) => {
    navigate(path);
    setQuery("");
    setSearchOpen(false);
  };

  const handleSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (results[0]) goTo(results[0].path);
  };

  const handleLogout = () => {
    setUserMenuOpen(false);
    logout();
    navigate("/login");
  };

  return (
    <header className="relative z-20 flex items-center justify-between gap-4 px-8 py-4 border-b border-white/10 bg-black backdrop-blur-sm">
      <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm min-w-0">
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;
          return (
            <span key={crumb.path} className="flex items-center gap-2 min-w-0">
              {index > 0 && <ChevronRight className="size-3.5 text-white/20 shrink-0" />}
              {isLast ? (
                <span className="text-white font-semibold truncate">{crumb.label}</span>
              ) : (
                <Link
                  to={crumb.path}
                  className="text-white/40 hover:text-white/70 transition-colors truncate"
                >
                  {crumb.label}
                </Link>
              )}
            </span>
          );
        })}
      </nav>

      <div className="flex items-center gap-3">
        <div ref={searchRef} className="relative">
          <form onSubmit={handleSearchSubmit}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-white/30" />
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSearchOpen(true);
              }}
              onFocus={() => setSearchOpen(true)}
              placeholder="Buscar en el panel..."
              className="w-64 rounded-full bg-white/5 border border-white/10 pl-9 pr-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-accent transition-colors"
            />
          </form>
          {searchOpen && trimmedQuery && (
            <div className="absolute right-0 mt-2 w-64 rounded-xl border border-white/10 bg-[#111] shadow-xl overflow-hidden z-40">
              {results.length === 0 ? (
                <p className="px-4 py-3 text-xs text-white/40">Sin resultados</p>
              ) : (
                results.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => goTo(item.path)}
                    className="w-full text-left px-4 py-2.5 text-sm text-white/80 hover:bg-white/5 transition-colors cursor-pointer"
                  >
                    {item.label}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        <div ref={notificationsRef} className="relative">
          <button
            onClick={() => setNotificationsOpen((open) => !open)}
            className="p-2 rounded-full hover:bg-white/5 text-white/60 hover:text-white transition-colors cursor-pointer"
            aria-label="Notificaciones"
          >
            <Bell size={18} />
          </button>
          {notificationsOpen && (
            <div className="absolute right-0 mt-2 w-72 rounded-xl border border-white/10 bg-[#111] shadow-xl p-4 z-40">
              <p className="text-xs font-semibold text-white/40 uppercase tracking-widest mb-2">
                Notificaciones
              </p>
              <p className="text-sm text-white/50">No tenés notificaciones nuevas.</p>
            </div>
          )}
        </div>

        <div ref={userMenuRef} className="relative">
          <button
            onClick={() => setUserMenuOpen((open) => !open)}
            className="flex items-center gap-3 pl-2 pr-1 py-1 rounded-full hover:bg-white/5 transition-colors cursor-pointer"
          >
            <div className="flex flex-col items-end">
              <span className="text-sm font-medium text-white">{user?.email}</span>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/50">
                <Shield size={12} />
                <span>{user?.role || "Usuario"}</span>
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-blue-600 to-purple-600 flex items-center justify-center border border-white/20 shrink-0">
              <User className="text-white" size={20} />
            </div>
            <ChevronDown
              size={14}
              className={`text-white/40 transition-transform ${userMenuOpen ? "rotate-180" : ""}`}
            />
          </button>
          {userMenuOpen && (
            <div className="absolute right-0 mt-2 w-56 rounded-xl border border-white/10 bg-[#111] shadow-xl overflow-hidden z-40">
              <div className="px-4 py-3 border-b border-white/10">
                <p className="text-sm font-medium text-white truncate">{user?.email}</p>
                <p className="text-xs text-white/40">{user?.role || "Usuario"}</p>
              </div>
              <Link
                to="/admin/settings"
                onClick={() => setUserMenuOpen(false)}
                className="flex items-center gap-2 px-4 py-2.5 text-sm text-white/70 hover:bg-white/5 hover:text-white transition-colors"
              >
                <Settings size={15} />
                Configuración
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-white/5 transition-colors cursor-pointer"
              >
                <LogOut size={15} />
                Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
